import { getTeamIcon } from '@/utils/teamIcons';
import { Shield, Trophy } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DisplayLeaderboardRow } from './DisplayLeaderboardRow';
import { DisplayPodium } from './DisplayPodium';

interface Team {
  id: string;
  name: string;
  iconName: string;
  iconColor: string;
  totalScore: number;
  challengesCompleted?: number;
}

interface AllianceEntry {
  allianceId: string;
  allianceName: string;
  allianceColor: string | null;
  allianceIconName: string | null;
  allianceIconColor: string | null;
  totalPoints: number;
  challengesCompleted: number;
  teamCount: number;
  rank: number;
}

interface DisplayLeaderboardProps {
  teams: Team[];
  alliances?: AllianceEntry[];
  pointsLabel: string;
}

type PositionChange = 'up' | 'down' | 'none';
type ViewMode = 'teams' | 'alliances';

/**
 * Display leaderboard with team and alliance views, styled in the
 * deck language (serif numerals, mono labels, surface cards on the
 * branded stage). Auto-cycles between views when alliances exist.
 */
export function DisplayLeaderboard({ teams, alliances = [], pointsLabel }: DisplayLeaderboardProps) {
  const hasAlliances = alliances.length > 0;

  const [viewMode, setViewMode] = useState<ViewMode>('teams');
  const cycleTimerRef = useRef<ReturnType<typeof setInterval>>(undefined);

  const startCycleTimer = useCallback(() => {
    if (cycleTimerRef.current) clearInterval(cycleTimerRef.current);
    cycleTimerRef.current = setInterval(() => {
      setViewMode((prev) => (prev === 'teams' ? 'alliances' : 'teams'));
    }, 15000);
  }, []);

  useEffect(() => {
    if (!hasAlliances) return;
    startCycleTimer();
    return () => {
      if (cycleTimerRef.current) clearInterval(cycleTimerRef.current);
    };
  }, [hasAlliances, startCycleTimer]);

  const handleViewSwitch = useCallback(
    (mode: ViewMode) => {
      setViewMode(mode);
      if (hasAlliances) startCycleTimer();
    },
    [hasAlliances, startCycleTimer],
  );

  const sortedTeams = [...teams].sort((a, b) => b.totalScore - a.totalScore);

  // Track position changes for the rank-shuffle animation
  const previousRanksRef = useRef<Map<string, number>>(new Map());
  const [positionChanges, setPositionChanges] = useState<Map<string, PositionChange>>(new Map());
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    if (sortedTeams.length === 0) return;
    const currentRanks = new Map<string, number>();
    sortedTeams.forEach((team, index) => currentRanks.set(team.id, index + 1));

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      previousRanksRef.current = currentRanks;
      return;
    }

    const changes = new Map<string, PositionChange>();
    currentRanks.forEach((currentRank, teamId) => {
      const previousRank = previousRanksRef.current.get(teamId);
      if (previousRank !== undefined && previousRank !== currentRank) {
        changes.set(teamId, currentRank < previousRank ? 'up' : 'down');
      }
    });

    if (changes.size > 0) {
      setPositionChanges(changes);
      const timer = setTimeout(() => setPositionChanges(new Map()), 2000);
      previousRanksRef.current = currentRanks;
      return () => clearTimeout(timer);
    }
    previousRanksRef.current = currentRanks;
  }, [sortedTeams]);

  const podiumTeams = sortedTeams.slice(0, 3);
  const remainingTeams = sortedTeams.slice(3);
  const sortedAlliances = [...alliances].sort((a, b) => b.totalPoints - a.totalPoints);

  const toggleButton = (mode: ViewMode, text: string) => (
    <button
      onClick={() => handleViewSwitch(mode)}
      className="rounded-full px-3 py-1 font-mono text-[0.625rem] font-medium uppercase tracking-[0.15em] transition-opacity"
      style={
        viewMode === mode
          ? { backgroundColor: 'var(--dsp-accent)', color: '#0f172a' }
          : { color: 'var(--dsp-soft)' }
      }
    >
      {text}
    </button>
  );

  return (
    <div className="flex h-full flex-col">
      {/* Section header — fixed height so it lines up exactly with the
          Live Activity header across the split. */}
      <div
        className="flex h-14 flex-shrink-0 items-center justify-between border-b px-6"
        style={{ borderColor: 'var(--dsp-line)' }}
      >
        <p
          className="font-mono text-[0.6875rem] font-medium uppercase tracking-[0.2em]"
          style={{ color: 'var(--dsp-accent-text)' }}
        >
          {viewMode === 'teams' ? 'Standings' : 'Alliances'}
        </p>

        {hasAlliances && (
          <div
            className="flex rounded-full border p-0.5"
            style={{ borderColor: 'var(--dsp-line)' }}
          >
            {toggleButton('teams', 'Teams')}
            {toggleButton('alliances', 'Alliances')}
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {viewMode === 'teams' ? (
          teams.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3" style={{ color: 'var(--dsp-soft)' }}>
              <Trophy className="h-12 w-12 opacity-40" />
              <p className="font-mono text-xs uppercase tracking-[0.2em]">
                Teams appear here once they join
              </p>
            </div>
          ) : (
            <>
              <div className="flex-shrink-0 pt-2">
                <DisplayPodium
                  teams={podiumTeams}
                  pointsLabel={pointsLabel}
                  positionChanges={positionChanges}
                />
              </div>

              {remainingTeams.length > 0 && (
                <div className="mt-3 min-h-0 flex-1 overflow-y-auto px-6 pb-6">
                  <div
                    className="divide-y overflow-hidden rounded-2xl border"
                    style={{
                      borderColor: 'var(--dsp-line)',
                      // divide color follows the hairline token
                      ['--tw-divide-opacity' as string]: 1,
                    }}
                  >
                    {remainingTeams.map((team, index) => (
                      <div key={team.id} style={{ borderColor: 'var(--dsp-line)' }}>
                        <DisplayLeaderboardRow
                          team={team}
                          rank={index + 4}
                          pointsLabel={pointsLabel}
                          striped={index % 2 === 0}
                          positionChange={positionChanges.get(team.id)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )
        ) : alliances.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3" style={{ color: 'var(--dsp-soft)' }}>
            <Shield className="h-12 w-12 opacity-40" />
            <p className="font-mono text-xs uppercase tracking-[0.2em]">No alliances</p>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
            <div
              className="divide-y overflow-hidden rounded-2xl border"
              style={{ borderColor: 'var(--dsp-line)' }}
            >
              {sortedAlliances.map((alliance, index) => {
                const AllianceIcon = getTeamIcon(alliance.allianceIconName || 'shield');
                const rank = index + 1;
                const isFirst = rank === 1;

                return (
                  <div
                    key={alliance.allianceId}
                    className="flex items-center gap-4 px-5 py-4"
                    style={{
                      borderColor: 'var(--dsp-line)',
                      backgroundColor:
                        index % 2 === 0
                          ? 'color-mix(in srgb, var(--dsp-text) 3%, transparent)'
                          : undefined,
                    }}
                  >
                    <span
                      className="w-10 flex-shrink-0 text-center text-3xl font-semibold tabular-nums"
                      style={{ color: isFirst ? 'var(--dsp-accent-text)' : 'var(--dsp-soft)' }}
                    >
                      {rank}
                    </span>

                    <div
                      className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: alliance.allianceIconColor || '#6366F1' }}
                    >
                      <AllianceIcon className="h-5 w-5 text-white" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-lg font-semibold">
                        {alliance.allianceName}
                      </span>
                      <span
                        className="font-mono text-[0.625rem] uppercase tracking-[0.15em]"
                        style={{ color: 'var(--dsp-soft)' }}
                      >
                        {alliance.teamCount} team{alliance.teamCount !== 1 ? 's' : ''} ·{' '}
                        {alliance.challengesCompleted} challenges
                      </span>
                    </div>

                    <div className="flex flex-shrink-0 items-baseline gap-1.5">
                      <span className="text-2xl font-semibold tabular-nums">
                        {alliance.totalPoints.toLocaleString()}
                      </span>
                      <span
                        className="font-mono text-[0.625rem] uppercase tracking-[0.15em]"
                        style={{ color: 'var(--dsp-soft)' }}
                      >
                        {pointsLabel}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
